// src/components/CategoryBanner.js
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getImageUrl } from '../utils/getImageUrl';

const TILE_COLORS = ['#2563EB', '#16A34A', '#DC2626', '#9333EA', '#EA580C', '#0891B2', '#DB2777', '#4F46E5'];

const tileColor = (tile) => tile.background_color || TILE_COLORS[(tile.category_id || 0) % TILE_COLORS.length];

export const CATEGORY_FONTS = [
  { value: '', label: 'Site default' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Nunito', label: 'Nunito' },
  { value: 'Oswald', label: 'Oswald' },
  { value: 'Roboto Slab', label: 'Roboto Slab' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Bebas Neue', label: 'Bebas Neue', singleWeight: true },
  { value: 'Anton', label: 'Anton', singleWeight: true },
  { value: 'Lobster', label: 'Lobster', singleWeight: true },
];

export const CATEGORY_NAME_POSITIONS = [
  'top-left', 'top-center', 'top-right',
  'center-left', 'center', 'center-right',
  'bottom-left', 'bottom-center', 'bottom-right',
];

export const CATEGORY_FONT_SIZES = { xs: [10, 12], sm: [12, 14], base: [14, 16], lg: [16, 18], xl: [18, 20], '2xl': [20, 24], '3xl': [24, 30] };

const FONT_WEIGHTS = { normal: 400, medium: 500, semibold: 600, bold: 700 };

const H_ALIGN = {
  left: 'items-start text-left',
  center: 'items-center text-center',
  right: 'items-end text-right'
};

const V_ALIGN = { top: 'justify-start', center: 'justify-center', bottom: 'justify-end' };

const IMAGE_SHADE = {
  top: 'bg-gradient-to-b from-black/75 via-black/20 to-transparent',
  center: 'bg-black/35',
  bottom: 'bg-gradient-to-t from-black/75 via-black/20 to-transparent'
};

export const loadCategoryFont = (family) => {
  const font = CATEGORY_FONTS.find(f => f.value === family);
  if (!font || !font.value || typeof document === 'undefined') return;
  const id = `category-font-${font.value.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${font.value.replace(/\s+/g, '+')}${font.singleWeight ? '' : ':wght@400;500;600;700'}&display=swap`;
  document.head.appendChild(link);
};

const hexToRgba = (hex, alpha) => {
  const clean = /^#[0-9a-fA-F]{6}$/.test(hex || '') ? hex.slice(1) : '000000';
  const value = parseInt(clean, 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

export const nameShadowStyle = (shadow, color) => {
  const c = (alpha) => hexToRgba(color, alpha);
  switch (shadow) {
    case 'none':
      return {};
    case 'medium':
      return { textShadow: `0 1px 2px ${c(0.9)}, 0 2px 8px ${c(0.7)}` };
    case 'strong':
      return { textShadow: `0 0 2px ${c(1)}, 0 1px 4px ${c(1)}, 0 2px 12px ${c(0.9)}` };
    case 'outline':
      return { textShadow: `-1px -1px 0 ${c(1)}, 1px -1px 0 ${c(1)}, -1px 1px 0 ${c(1)}, 1px 1px 0 ${c(1)}, 0 2px 4px ${c(0.6)}` };
    case 'box':
      return { backgroundColor: c(0.55), padding: '3px 7px', borderRadius: '4px' };
    default:
      return { textShadow: `0 1px 3px ${c(0.6)}` };
  }
};

export const visibleCategoryTiles = (banner, activeCategoryId = '', categoryCounts = null) => {
  let tiles = banner?.category_tiles || [];
  if (categoryCounts) {
    tiles = tiles.filter(tile => (categoryCounts[tile.category_id] || 0) > 0);
  }
  if (activeCategoryId) {
    const mode = banner?.category_when_filtered || 'show_all';
    if (mode === 'hide') return [];
    if (mode === 'selected_only') {
      return tiles.filter(tile => String(tile.category_id) === String(activeCategoryId));
    }
  }
  return tiles;
};

const parsePosition = (position) => {
  if (position === 'center') return { v: 'center', h: 'center' };
  const [v, h] = (position || 'bottom-left').split('-');
  return { v: V_ALIGN[v] ? v : 'bottom', h: H_ALIGN[h] ? h : 'left' };
};

/**
 * CategoryBanner - Category tiles for a banner of type 'categories'.
 * Clicking a tile filters the catalog by that category (clicking the active one clears it).
 * Layouts: grid (image tiles), table (compact cells), slider (horizontal scroll, rows stacked).
 */
const CategoryBanner = ({
  banner,
  isMobile = false,
  activeCategoryId = '',
  onCategorySelect = null,
  categoryCounts = null,
  className = ''
}) => {
  const navigate = useNavigate();
  const scrollerRef = useRef(null);
  const [expanded, setExpanded] = useState(true);
  const [imageError, setImageError] = useState({});
  const [canScroll, setCanScroll] = useState({ left: false, right: false });
  const [gridEl, setGridEl] = useState(null);
  const [gridWidth, setGridWidth] = useState(0);

  const tiles = useMemo(
    () => visibleCategoryTiles(banner, activeCategoryId, categoryCounts),
    [banner, activeCategoryId, categoryCounts]
  );

  const layout = banner?.category_layout || 'grid';
  const columns = Math.max(
    1,
    parseInt(isMobile ? (banner?.category_columns_mobile || 3) : (banner?.category_columns || 6), 10) || 1
  );
  const rows = Math.max(0, parseInt(banner?.category_rows || 0, 10) || 0);
  const showNames = banner?.category_show_names !== false;
  const showCounts = !!banner?.category_show_counts && !!categoryCounts;

  const fontFamily = banner?.category_font_family || '';
  const namePosition = banner?.category_name_position || 'bottom-left';
  const nameBelow = namePosition === 'below';
  const { v: nameV, h: nameH } = parsePosition(namePosition);
  const fontSizes = CATEGORY_FONT_SIZES[banner?.category_font_size] || CATEGORY_FONT_SIZES.sm;
  const nameStyle = {
    fontFamily: fontFamily ? `'${fontFamily}', sans-serif` : undefined,
    fontSize: `${isMobile ? fontSizes[0] : fontSizes[1]}px`,
    fontWeight: FONT_WEIGHTS[banner?.category_font_weight] || FONT_WEIGHTS.semibold,
    textTransform: banner?.category_uppercase ? 'uppercase' : undefined,
    lineHeight: 1.15
  };
  const nameColor = banner?.category_text_color || (nameBelow ? '#1F2937' : '#FFFFFF');
  const imageFit = banner?.category_image_fit === 'contain' ? 'contain' : 'cover';
  const hoverZoom = banner?.category_hover_zoom !== false;
  const shadowStyle = nameBelow && !banner?.category_text_color
    ? {}
    : nameShadowStyle(banner?.category_text_shadow || 'soft', banner?.category_shadow_color || '#000000');
  const [ratioW, ratioH] = (/^\d+:\d+$/.test(banner?.category_tile_ratio || '') ? banner.category_tile_ratio : '4:3')
    .split(':').map(Number);

  useEffect(() => {
    loadCategoryFont(fontFamily);
  }, [fontFamily]);

  const spanOf = useCallback((tile) => ({
    cols: layout === 'grid' ? Math.min(Math.max(parseInt(tile.col_span, 10) || 1, 1), columns) : 1,
    rows: layout === 'grid' ? Math.min(Math.max(parseInt(tile.row_span, 10) || 1, 1), 2) : 1
  }), [layout, columns]);

  const cellLimit = layout !== 'slider' && rows > 0 ? rows * columns : 0;
  const collapsedTiles = useMemo(() => {
    if (!cellLimit) return tiles;
    const kept = [];
    let usedCells = 0;
    for (const tile of tiles) {
      const { cols, rows: tileRows } = spanOf(tile);
      usedCells += cols * tileRows;
      if (usedCells > cellLimit && kept.length > 0) break;
      kept.push(tile);
    }
    return kept;
  }, [tiles, cellLimit, spanOf]);
  const isTruncated = collapsedTiles.length < tiles.length;
  const visibleTiles = isTruncated && !expanded ? collapsedTiles : tiles;

  useEffect(() => {
    if (!gridEl || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(entries => {
      setGridWidth(entries[0]?.contentRect?.width || 0);
    });
    observer.observe(gridEl);
    return () => observer.disconnect();
  }, [gridEl]);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    });
  }, []);

  useEffect(() => {
    if (layout !== 'slider') return undefined;
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [layout, tiles.length, columns, updateScrollState]);

  const scrollByPage = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
  };

  const handleSelect = (tile) => {
    const id = String(tile.category_id);
    if (onCategorySelect) {
      onCategorySelect(String(activeCategoryId) === id ? '' : id);
    } else {
      navigate(`/products?category=${id}`);
    }
  };

  const handleLinkClick = (url, target = '_self') => {
    if (!url) return;
    const isInternal = url.startsWith('/') || (!url.startsWith('http://') && !url.startsWith('https://'));
    if (isInternal) {
      navigate(url);
    } else {
      window.open(url, target);
    }
  };

  if (!banner || tiles.length === 0) return null;

  const tileImage = (tile) => {
    if (!tile.image_url || imageError[tile.id]) return null;
    return getImageUrl(tile.image_url || tile.thumbnail_url);
  };

  const countOf = (tile) => (categoryCounts ? categoryCounts[tile.category_id] || 0 : 0);

  const renderName = (tile, showName, scale = 1) => {
    const namePx = Math.round(parseInt(nameStyle.fontSize, 10) * scale);
    return (
      <span className="flex flex-col max-w-full" style={shadowStyle}>
        {showName && (
          <span
            className={`block ${nameBelow ? 'line-clamp-2' : 'line-clamp-3'} break-words`}
            style={{ ...nameStyle, fontSize: `${namePx}px`, color: nameColor }}
          >
            {tile.name}
          </span>
        )}
        {showCounts && (
          <span
            className="block mt-0.5"
            style={{
              fontFamily: nameStyle.fontFamily,
              fontSize: `${Math.max(9, Math.round(namePx * 0.75))}px`,
              color: nameColor,
              opacity: 0.8
            }}
          >
            {countOf(tile)} products
          </span>
        )}
      </span>
    );
  };

  const selectedBannerTile = activeCategoryId
    && banner.category_when_filtered === 'selected_only'
    && tiles.length === 1
    && tiles[0].banner_image_url
    && !imageError[`banner-${tiles[0].id}`]
    ? tiles[0]
    : null;

  const renderSelectedBanner = (tile) => {
    const showName = banner.category_banner_show_name !== false;
    const hasText = showName || showCounts;
    return (
      <div className="p-2">
        <div className="relative w-full rounded-md overflow-hidden">
          <img
            src={getImageUrl(tile.banner_image_url)}
            alt={tile.name}
            className="block w-full h-auto"
            onError={() => setImageError(prev => ({ ...prev, [`banner-${tile.id}`]: true }))}
          />
          {!nameBelow && hasText && (
            <>
              <div className={`absolute inset-0 ${IMAGE_SHADE[nameV]}`} />
              <div className={`absolute inset-0 flex flex-col p-3 sm:p-5 ${V_ALIGN[nameV]} ${H_ALIGN[nameH]}`}>
                {renderName(tile, showName, isMobile ? 1.25 : 1.6)}
              </div>
            </>
          )}
        </div>
        {nameBelow && hasText && (
          <div className={`w-full pt-1 flex flex-col ${H_ALIGN.center}`}>
            {renderName(tile, showName, isMobile ? 1.25 : 1.6)}
          </div>
        )}
      </div>
    );
  };

  const renderImageTile = (tile, gridStyle = null) => {
    const isActive = String(activeCategoryId) === String(tile.category_id);
    const src = tileImage(tile);
    const showName = showNames || !src;
    const hasText = showName || showCounts;
    const nameText = () => renderName(tile, showName);
    return (
      <button
        key={tile.id}
        type="button"
        onClick={() => handleSelect(tile)}
        title={tile.name}
        aria-pressed={isActive}
        className={`group w-full flex flex-col focus:outline-none ${gridStyle?.gridAutoFill ? 'h-full' : ''}`}
        style={gridStyle ? { gridColumn: gridStyle.gridColumn, gridRow: gridStyle.gridRow } : undefined}
      >
        <div
          className={`relative w-full ${gridStyle?.gridAutoFill ? 'flex-1 min-h-0' : ''} rounded-md overflow-hidden transition-shadow ${
            isActive ? 'ring-2 ring-offset-1 ring-blue-600' : 'group-hover:shadow-md'
          }`}
          style={{
            aspectRatio: gridStyle?.gridAutoFill ? undefined : `${ratioW} / ${ratioH}`,
            backgroundColor: src ? (imageFit === 'contain' ? tile.background_color || '#F9FAFB' : undefined) : tileColor(tile)
          }}
        >
          {src && (
            <img
              src={src}
              alt={tile.name}
              className={`absolute inset-0 w-full h-full transition-transform duration-300 ${hoverZoom ? 'group-hover:scale-105' : ''}`}
              style={{ objectFit: imageFit, objectPosition: tile.image_position || 'center' }}
              onError={() => setImageError(prev => ({ ...prev, [tile.id]: true }))}
            />
          )}
          {!nameBelow && hasText && (
            <>
              {src && <div className={`absolute inset-0 ${IMAGE_SHADE[nameV]}`} />}
              <div className={`absolute inset-0 flex flex-col p-2 ${V_ALIGN[nameV]} ${H_ALIGN[nameH]}`}>
                {nameText()}
              </div>
            </>
          )}
        </div>
        {nameBelow && hasText && (
          <div className={`w-full pt-1 px-0.5 flex flex-col ${H_ALIGN.center}`}>
            {nameText()}
          </div>
        )}
      </button>
    );
  };

  const renderTableCell = (tile) => {
    const isActive = String(activeCategoryId) === String(tile.category_id);
    const src = tileImage(tile);
    return (
      <button
        key={tile.id}
        type="button"
        onClick={() => handleSelect(tile)}
        title={tile.name}
        aria-pressed={isActive}
        className={`flex items-center gap-2 px-2 py-1.5 text-left min-w-0 focus:outline-none transition-colors ${
          isActive ? 'bg-blue-50 text-blue-700' : 'bg-white hover:bg-gray-50 text-gray-800'
        }`}
      >
        {src ? (
          <img
            src={src}
            alt=""
            className="w-8 h-8 rounded object-cover flex-shrink-0"
            onError={() => setImageError(prev => ({ ...prev, [tile.id]: true }))}
          />
        ) : (
          <span
            className="w-2 h-8 rounded-sm flex-shrink-0"
            style={{ backgroundColor: tileColor(tile) }}
          />
        )}
        <span
          className="flex-1 truncate"
          style={{
            fontFamily: nameStyle.fontFamily,
            fontSize: nameStyle.fontSize,
            fontWeight: isActive ? Math.max(nameStyle.fontWeight, 600) : nameStyle.fontWeight,
            textTransform: nameStyle.textTransform
          }}
        >
          {tile.name}
        </span>
        {showCounts && (
          <span className="text-[10px] sm:text-xs text-gray-500 flex-shrink-0">{countOf(tile)}</span>
        )}
      </button>
    );
  };

  const gap = isMobile ? 6 : 8;
  const namePx = parseInt(nameStyle.fontSize, 10);
  const belowExtra = nameBelow
    ? (showNames ? Math.ceil(namePx * 1.15 * 2) : Math.ceil(namePx * 1.15)) + (showCounts ? Math.ceil(Math.max(9, namePx * 0.75) * 1.3) + 2 : 0) + 6 + (shadowStyle.padding ? 6 : 0)
    : 0;
  const cellWidth = gridWidth > 0 ? (gridWidth - (columns - 1) * gap) / columns : 0;
  const rowHeight = layout === 'grid' && cellWidth > 0 ? Math.round(cellWidth * ratioH / ratioW + belowExtra) : 0;

  return (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
      {(banner.title || banner.link_url || activeCategoryId) && (
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-800 truncate">{banner.title}</h3>
            {banner.description && (
              <p className="text-xs text-gray-500 truncate">{banner.description}</p>
            )}
          </div>
          <div className="flex items-center gap-3 ml-2">
            {onCategorySelect && activeCategoryId && tiles.some(tile => String(tile.category_id) === String(activeCategoryId)) && (
              <button
                type="button"
                onClick={() => onCategorySelect('')}
                className="text-xs text-red-600 hover:text-red-800 whitespace-nowrap"
              >
                {banner.category_when_filtered === 'selected_only' ? 'Show all categories' : 'Show all products'}
              </button>
            )}
            {banner.link_url && (
              <button
                type="button"
                onClick={() => handleLinkClick(banner.link_url, banner.link_target)}
                className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
              >
                View all
              </button>
            )}
          </div>
        </div>
      )}

      {selectedBannerTile ? (
        renderSelectedBanner(selectedBannerTile)
      ) : layout === 'slider' ? (
        <div className="relative px-2 py-2">
          <div
            ref={scrollerRef}
            onScroll={updateScrollState}
            className="overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div
              className="grid grid-flow-col"
              style={{
                gap: `${gap}px`,
                gridTemplateRows: `repeat(${Math.max(1, rows)}, auto)`,
                gridAutoColumns: `calc((100% - ${(columns - 1) * gap}px) / ${columns})`
              }}
            >
              {tiles.map(tile => (
                <div key={tile.id} className="snap-start">
                  {renderImageTile(tile)}
                </div>
              ))}
            </div>
          </div>

          {canScroll.left && (
            <button
              type="button"
              onClick={() => scrollByPage(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 rounded-full p-1 z-20 focus:outline-none"
              aria-label="Previous categories"
            >
              <FaChevronLeft className="w-3 h-3 text-white" />
            </button>
          )}
          {canScroll.right && (
            <button
              type="button"
              onClick={() => scrollByPage(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 rounded-full p-1 z-20 focus:outline-none"
              aria-label="Next categories"
            >
              <FaChevronRight className="w-3 h-3 text-white" />
            </button>
          )}
        </div>
      ) : layout === 'table' ? (
        <div
          className="grid bg-gray-200 gap-px border-b"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {visibleTiles.map(renderTableCell)}
          {Array.from({ length: (columns - (visibleTiles.length % columns)) % columns }).map((_, idx) => (
            <div key={`pad-${idx}`} className="bg-white" />
          ))}
        </div>
      ) : (
        <div
          ref={setGridEl}
          className="grid p-2"
          style={{
            gap: `${gap}px`,
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            gridAutoRows: rowHeight ? `${rowHeight}px` : undefined,
            gridAutoFlow: 'row dense'
          }}
        >
          {visibleTiles.map(tile => {
            const { cols, rows: tileRows } = spanOf(tile);
            return renderImageTile(tile, {
              gridColumn: `span ${cols}`,
              gridRow: `span ${tileRows}`,
              gridAutoFill: rowHeight > 0
            });
          })}
        </div>
      )}

      {isTruncated && (
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          className="w-full text-xs text-blue-600 hover:text-blue-800 hover:bg-gray-50 py-1.5 border-t"
        >
          {expanded ? 'Show less' : `Show all ${tiles.length} categories`}
        </button>
      )}
    </div>
  );
};

export default CategoryBanner;
